import { Component, OnInit } from '@angular/core';
import { default as validationEngine } from 'devextreme/ui/validation_engine';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { alert, confirm } from 'devextreme/ui/dialog';
import { AuthService, AppInformationService, GenesisUnitOfWork, UtilityService } from 'app/core/core.module';
import { ExpenseReportLine } from 'app/core/model/expense-report-line';
import { ExpenseReport } from 'app/core/model/expense-report';
import { ExpenseType } from 'app/core/model/expense-type';
import { PictureService } from 'app/core/picture.service';
import { ExpenseReportLineAttachment } from 'app/core/model/expense-report-line-attachment';
import { environment } from 'environments/environment';

@Component({
    selector: 'app-expense-report-dialog',
    templateUrl: './expense-report-dialog.component.html',
    styleUrls: ['./expense-report-dialog.component.scss'],
    standalone: false,
})
export class ExpenseReportLineDialogComponent implements OnInit {
    result: Subject<any> = new Subject<any>();
    expenseLine: ExpenseReportLine;
    expenseReport: any;
    expenseTypes: ExpenseType[];
    attachments: ExpenseReportLineAttachment[];

    validationGroup = 'mainValidationGroup';
    errorAction = 'submitting';

    uploadHeader: any;
    uploadUrl = `${environment.serviceName}/file/SaveExpenseReportLineAttachmentToBlobStorage`;

    constructor(
        public modalRef: BsModalRef,
        private uow: GenesisUnitOfWork,
        private appInfo: AppInformationService,
        private auth: AuthService,
        private utility: UtilityService,
        private pictureService: PictureService
    ) {}

    async ngOnInit() {
        this.uploadHeader = { Authorization: `Bearer ${this.auth.currentUser.jwtToken}` };
        if (!this.expenseLine) {
            this.expenseLine = this.uow.expenseReportLines.createEntity({
                created: new Date(),
                createdBy: this.auth.currentUser.userName,
                expenseDate: new Date(),
                expenseTypeId: null,
                expenseReport: this.expenseReport,
            }) as ExpenseReportLine;
        }
        this.attachments = this.expenseLine.attachments.filter(x => !x.deleted);
        this.expenseTypes = (await this.uow.expenseTypes.whereActive()) as ExpenseType[];
    }

    async save() {
        if (!this.validate()) return;
        this.result.next({ success: true });
    }

    close() {
        this.expenseLine.entityAspect.rejectChanges();
        this.result.next({ success: false });
    }

    validate() {
        const groupConfig = validationEngine.getGroupConfig('expenseDialogValidationGroup');
        if (groupConfig) {
            const result = validationEngine.validateGroup('expenseDialogValidationGroup');
            if (!result.isValid) {
                let message = '';
                result.brokenRules.forEach((brokenRule) => {
                    if (brokenRule.type === 'custom') message = `${brokenRule.message}\n${message}`;
                });
                this.appInfo.notifyToast(`Please fix errors before saving.\n\n${message}`, 'error');
                return false;
            }
        }
        return true;
    }

    uploadStarted(e) {
        this.appInfo.pleaseWaitShow();
    }

    async uploaded(e) {
        if (e?.request?.responseText) {
            const fileResponses = JSON.parse(e.request.responseText) as { filename: string; name: string }[];
            for (const fileResponse of fileResponses) {
                const attachment = this.uow.expenseReportLineAttachments.createEntity({
                    expenseReportLineId: this.expenseLine.id,
                    name: fileResponse.name,
                    fileName: fileResponse.filename,
                    created: new Date(),
                    createdBy: this.auth.currentUser.userName,
                }) as ExpenseReportLineAttachment;
                this.expenseLine.attachments.push(attachment);
                await this.uow.commit([attachment]);
            }
        }
        this.attachments = this.expenseLine.attachments.filter(x => !x.deleted);
        this.appInfo.pleaseWaitHide();
    }

    uploadError(e) {
        this.appInfo.pleaseWaitHide();
        alert('An error occurred while attempting to upload the file(s)', 'Upload Error');
    }

    async removeAttachment(e: any, attachment: ExpenseReportLineAttachment) {
        e.preventDefault();
        attachment.deleted = new Date();
        attachment.deletedBy = this.auth.currentUser.userName;
        await this.uow.commit([attachment]);
        this.attachments = this.expenseLine.attachments.filter(x => !x.deleted);
    }

    openAttachment(attachment) {
        window.open(attachment.fileName, '_blank');
    }
}
